import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

import { config, isLocal } from "./config";

/* Client-side error reporting.
 *
 * No Shoku client had any until now — every bug was diagnosed from Cloud
 * Logging, which only ever sees the server half. A browser client changes that
 * calculus: the device and OS matrix is unknown, and the CSP is hand-maintained,
 * so its failure mode is total and visible ONLY on the deployed origin.
 *
 * Two destinations, deliberately. Sentry is the durable one (issue #99, the
 * seam this file was left open for); the in-memory ring buffer below stays
 * because it is the only thing a support conversation can read back on a device
 * nobody can attach a debugger to, and it keeps working when the CSP is the
 * thing that is broken.
 */

/* ⚠️ `connect-src` in firebase.json must name this DSN's ingest origin, or the
 * browser refuses the POST and the SDK has no way to report that it cannot
 * report. tests/headers.spec.ts parses the origin back out of this string and
 * asserts it, so the two cannot drift.
 *
 * The DSN is public by design — write-only, no read access — exactly like the
 * Firebase web key and the Places browser key in config.ts and the Amplitude
 * ingestion key in analytics.ts. */
const DSN =
  "https://c84bda7582cef7e4e028f0cdc3c4e5a1@o4512124541272064.ingest.de.sentry.io/4512124604055632";

declare global {
  interface Window {
    __SHOKU_SENTRY__?: boolean;
  }
}

/* At MODULE scope, not inside installErrorReporting(). A function call happens
 * after every import in main.tsx has been evaluated, so a module body that threw
 * on the way in would do so before Sentry's global handlers existed. This runs
 * the moment the module is imported — which is why main.tsx imports it first. */
Sentry.init({
  dsn: DSN,
  /* OFF on loopback, the same gate analytics.ts applies to Amplitude: `npm run
   * dev` and the whole Playwright suite would otherwise flood the project, and
   * the suite throws on purpose. The override is only ever read here and only
   * under isLocal, so it is inert on the deployed origin; set it from a
   * Playwright addInitScript to verify locally — a console command is too late,
   * because this runs on load. */
  enabled: !isLocal || window.__SHOKU_SENTRY__ === true,
  environment: config.env,
  integrations: [
    /* Supplies the hooks that name a transaction by its MATCHED ROUTE PATTERN
     * (/jobs/:jobId) rather than its URL (/jobs/job_8f21) — without which every
     * job, booking and conversation a contractor opens is its own transaction
     * name and nothing aggregates. Pairs with wrapCreateBrowserRouterV6 in
     * router.tsx; both halves are required. */
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      matchRoutes,
      createRoutesFromChildren,
    }),
  ],
  tracesSampleRate: config.env === "prod" ? 0.2 : 1.0,
  /* EMPTY, deliberately. Propagation adds `sentry-trace` and `baggage` headers
   * to outgoing requests, and neither contractor-api (main.go:209) nor
   * conversation-api (main.go:132) lists them in Access-Control-Allow-Headers —
   * so every single API call would fail its CORS preflight. The default value
   * matches the string "localhost", which would break `just contractor` too.
   * Widen only once both Go APIs run the Sentry SDK and their allow-lists name
   * both headers. */
  tracePropagationTargets: [],
  /* POPIA, and explicit because the DEFAULT MOVES: in SDK v10 an absent
   * `dataCollection` falls back to the legacy `sendDefaultPii: false`, but v11
   * flips `userInfo` to true — so a routine upgrade would quietly start
   * attaching contractor IP addresses. Same argument as the conservative replay
   * mask level in analytics.ts, on a site that handles bank account and ID
   * numbers. */
  dataCollection: {
    userInfo: false,
    httpBodies: [],
  },
});

export interface CapturedError {
  at: string;
  kind: "error" | "unhandledrejection" | "csp" | "manual";
  message: string;
  detail?: string;
}

const BUFFER_LIMIT = 50;
const buffer: CapturedError[] = [];

function record(entry: CapturedError): void {
  buffer.push(entry);
  if (buffer.length > BUFFER_LIMIT) buffer.shift();
  // Kept as a console error so the browser's own grouping, stack decoding and
  // "copy as text" all keep working — a custom logger would lose all three.
  // Sentry keeps it too, as a breadcrumb on whatever is reported next.
  console.error(`[shoku:${entry.kind}]`, entry.message, entry.detail ?? "");
}

export function captureError(message: string, detail?: unknown): void {
  const stringified = detail === undefined ? undefined : String(detail);
  record({ at: new Date().toISOString(), kind: "manual", message, detail: stringified });
  /* The LABEL is the half that makes an issue triageable — "Chat listener
   * failed" says where we are, "FirebaseError: permission-denied" does not — so
   * it becomes the exception, and the caught value becomes its `cause`, which
   * the SDK's linkedErrors integration renders as a chained exception. `extra`
   * carries the stringified form as well, because a cause that is not an Error
   * (a bare string, a Response) is not followed. */
  Sentry.captureException(new Error(message, { cause: detail }), {
    extra: { detail: stringified },
  });
}

/** Everything captured this session, newest last. Exposed for support triage. */
export function capturedErrors(): readonly CapturedError[] {
  return buffer;
}

let installed = false;

export function installErrorReporting(): void {
  if (installed) return;
  installed = true;

  /* ⚠️ The two window listeners below feed the BUFFER ONLY. Sentry's own
   * globalHandlers integration is already bound to `error` and
   * `unhandledrejection` by Sentry.init above, so reporting them again here
   * would file every unhandled failure twice. */
  window.addEventListener("error", (event) => {
    record({
      at: new Date().toISOString(),
      kind: "error",
      message: event.message || "Unknown error",
      detail: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as unknown;
    record({
      at: new Date().toISOString(),
      kind: "unhandledrejection",
      message: reason instanceof Error ? reason.message : String(reason),
      detail: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // A CSP mistake is invisible locally (the Vite dev server sends no CSP) and
  // fatal in production, so it gets its own listener rather than being left to
  // surface as a generic network failure somewhere downstream.
  //
  // ⚠️ Buffer only, and it must stay that way: a policy that blocks the Sentry
  // ingest origin fires this listener for the blocked POST itself, so reporting
  // from here would spin — violation, send, blocked, violation. The static
  // assertion in tests/headers.spec.ts is what guards that origin instead.
  document.addEventListener("securitypolicyviolation", (event) => {
    record({
      at: new Date().toISOString(),
      kind: "csp",
      message: `Blocked ${event.violatedDirective}: ${event.blockedURI}`,
      detail: event.sourceFile ? `${event.sourceFile}:${event.lineNumber}` : undefined,
    });
  });

  if (config.env !== "prod") {
    (window as unknown as { __SHOKU_ERRORS__: () => readonly CapturedError[] }).__SHOKU_ERRORS__ =
      capturedErrors;
  }
}
